import React, { Component } from 'react';
import ActionModal, { ActionModalFooter } from '../../../../common-components/modal/ActionModal';
import { Paragraph, SubTitle, Text } from '../../../../common-components/typography';
import SpinningLoader from '../../../../common-components/loading/SpinningLoader';
import { HyperlinkButton, PrimaryButton } from '../../../../common-components/buttons';
import { dispatch, IRootDispatch, state } from 'stores/rematch/root-store';
import _ from 'lodash';
import { Col, Icon, Row, Tooltip } from 'antd';
import { PaymentMethodTag } from 'common-components/tags';
import CommonUtils from 'utilities/common-utils';
import { FilterSection } from 'common-components/filter';
import { FilterType } from 'utilities/enum-utils';
import { connect } from 'react-redux';
import { Menu, Popover } from '@blueprintjs/core';
import WaiveLineItemActionModel from 'views/billings/payments/components/WaiveLineItemActionModel';
import moment from 'moment';
import InvoiceNoteModal from './InvoiceNoteModal';
import { InvoiceNoteModalType, PreviewInvoicesModalType } from '../../utils/constants';
import { ICreatingInvoiceNote } from 'src/interfaces/booking-interfaces';

interface IPreviewInvoicesModalProps {
  isOpen: boolean;
  onClose(): void;
  modalType?: PreviewInvoicesModalType;
  selectedBillingLineItem: typeof state.billingsStore.selectedBillingLineItem;
  invoiceNotes?: ICreatingInvoiceNote[];
  setSelectedCustomersForFilter: typeof dispatch.customersStore.setSelectedCustomersForFilter;
  onModifyInvoiceNotes?(invoiceNotes: ICreatingInvoiceNote[]): void;
  isNonBillable?: boolean;
}

interface IInvoice {
  paymentMethod: 'NDIA' | 'PLAN' | 'SELF';
  userId: string;
  firstName: string;
  lastName: string;
  bookingBillingLineItemId: string;
  totalWithDiscount: string | number;
  total: string | number;
}

interface IPreviewInvoicesModalState {
  isLoading: boolean;
  isWaiveModalOpen: boolean;
  isInvoiceNoteModalOpen: boolean;
  invoiceNoteModalType: InvoiceNoteModalType;
  modifyingInvoiceNotes: ICreatingInvoiceNote[];
  invoices: IInvoice[][];
  selectedLineItems: any;
  currentCustomerId: string;
  currentPaymentMethod: string;
  activeFilters: Array<any>;
  selectedInvoice: any;
}

const availableFilters = [FilterType.CUSTOM_CUSTOMER, FilterType.PAYMENT_METHODS];

class PreviewInvoicesModal extends Component<IPreviewInvoicesModalProps, IPreviewInvoicesModalState> {
  state: IPreviewInvoicesModalState = {
    selectedLineItems: [],
    currentCustomerId: '',
    currentPaymentMethod: '',
    isLoading: false,
    isWaiveModalOpen: false,
    isInvoiceNoteModalOpen: false,
    invoiceNoteModalType: InvoiceNoteModalType.ADD,
    modifyingInvoiceNotes: [],
    invoices: [],
    activeFilters: [
      {
        filter: FilterType.CUSTOM_CUSTOMER,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.CUSTOM_CUSTOMER).fullSelectionName,
      },
      {
        filter: FilterType.PAYMENT_METHODS,
        values: [],
        selectionLabel: CommonUtils.getFilterSettings(FilterType.PAYMENT_METHODS).fullSelectionName,
      },
    ],
    selectedInvoice: null,
  };

  private _onCloseModal = () => {
    const { onClose } = this.props;
    if (!this.state.isLoading) {
      this.setState({
        isLoading: false,
        activeFilters: [
          {
            filter: FilterType.CUSTOM_CUSTOMER,
            values: [],
            selectionLabel: CommonUtils.getFilterSettings(FilterType.CUSTOM_CUSTOMER).fullSelectionName,
          },
          {
            filter: FilterType.PAYMENT_METHODS,
            values: [],
            selectionLabel: CommonUtils.getFilterSettings(FilterType.PAYMENT_METHODS).fullSelectionName,
          },
        ],
      });
      onClose();
    }
  };

  private _onSaveChanges = () => {
    this.props.onModifyInvoiceNotes(this.state.modifyingInvoiceNotes);
    this._onCloseModal();
  };

  private _modifyNotes = (invoiceNotes: ICreatingInvoiceNote[]) => {
    this.setState({
      modifyingInvoiceNotes: invoiceNotes,
      isInvoiceNoteModalOpen: false,
      currentCustomerId: '',
      currentPaymentMethod: '',
    });
  };

  private _onChangeFilter = (filters) => {
    this.setState({ activeFilters: filters });
  };

  private _filterInvoices = (activeFilters) => {
    const { selectedBillingLineItem } = this.props;
    const customerFilter = _.find(activeFilters, (filter) => filter.filter === FilterType.CUSTOM_CUSTOMER);
    const paymentMethodFilter = _.find(activeFilters, (filter) => filter.filter === FilterType.PAYMENT_METHODS);
    const filteredByCustomerLineItems =
      customerFilter && customerFilter.values.length > 0
        ? _.filter(selectedBillingLineItem, (lineItem) =>
            _.find(customerFilter.values, (customerUserId) => customerUserId === lineItem.userId),
          )
        : selectedBillingLineItem;
    const filteredLineItems =
      paymentMethodFilter && paymentMethodFilter.values.length > 0
        ? _.filter(filteredByCustomerLineItems, (lineItem) =>
            _.find(paymentMethodFilter.values, (paymentMethod) => paymentMethod === lineItem.paymentMethod),
          )
        : filteredByCustomerLineItems;
    return _.orderBy(
      _.groupBy(filteredLineItems, (lineItem) => lineItem.paymentMethod + '|' + lineItem.userId),
      'lastName',
    );
  };

  private _openInvoicePreview = async (lineItems: string[], userId?: string, paymentMethod?: string) => {
    const currentTime = moment().format('mm-ss-SSS');
    const previewingNote = this.state.modifyingInvoiceNotes.find(
      (invoiceNote) => invoiceNote.userId === userId && invoiceNote.paymentMethod === paymentMethod,
    );

    if (previewingNote) {
      localStorage.setItem(`previewInvoiceNotes-${currentTime}`, previewingNote.note);
    }
    localStorage.setItem(`previewInvoiceLineItems-${currentTime}`, lineItems.join(','));

    window.open(`/pdf?type=preview-invoice-by-line-items&time=${currentTime}`, '_blank');
  };

  private _openWaiveInvoiceModal = (invoice) => {
    this.setState({ isWaiveModalOpen: true, selectedInvoice: invoice });
  };

  private _closeWaiveInvoiceModal = () => {
    this.setState({ isWaiveModalOpen: false });
  };

  private _openNoteModal = (customerId: string, paymentMethod: string, invoiceNoteModalType: InvoiceNoteModalType) => {
    this.setState({
      isInvoiceNoteModalOpen: true,
      currentCustomerId: customerId,
      invoiceNoteModalType,
      currentPaymentMethod: paymentMethod,
    });
  };

  private _closeAddNoteModal = () => {
    this.setState({ isInvoiceNoteModalOpen: false, currentCustomerId: '', currentPaymentMethod: '' });
  };

  componentDidMount = async () => {
    const { setSelectedCustomersForFilter, selectedBillingLineItem } = this.props;
    const { activeFilters } = this.state;
    this.setState({ isLoading: true });
    await setSelectedCustomersForFilter(
      _.map(_.uniqBy(selectedBillingLineItem, 'userId'), (lineItem: any) => {
        return {
          userId: lineItem.userId,
          lastName: lineItem.lastName,
          firstName: lineItem.firstName,
        };
      }),
    );
    this.setState({ isLoading: false, invoices: this._filterInvoices(activeFilters) });
  };

  componentDidUpdate = async (
    prevProps: Readonly<IPreviewInvoicesModalProps>,
    prevState: Readonly<IPreviewInvoicesModalState>,
    snapshot?: any,
  ) => {
    const { setSelectedCustomersForFilter, selectedBillingLineItem, invoiceNotes = [], isOpen } = this.props;
    const { activeFilters } = this.state;
    if (prevProps.selectedBillingLineItem !== selectedBillingLineItem) {
      const invoices = _.orderBy(
        _.groupBy(selectedBillingLineItem, (lineItem) => lineItem.paymentMethod + '|' + lineItem.userId),
        'lastName',
      );
      await setSelectedCustomersForFilter(
        _.map(_.uniqBy(selectedBillingLineItem, 'userId'), (lineItem: any) => {
          return {
            userId: lineItem.userId,
            lastName: lineItem.lastName,
            firstName: lineItem.firstName,
          };
        }),
      );
      this.setState({ isLoading: false, invoices });
    }
    if (
      prevProps.selectedBillingLineItem &&
      prevProps.selectedBillingLineItem.length > 0 &&
      (!selectedBillingLineItem || selectedBillingLineItem.length === 0)
    ) {
      this._onCloseModal();
    }
    if (prevState.activeFilters !== activeFilters) {
      this.setState({ invoices: this._filterInvoices(activeFilters) });
    }
    if (!prevProps.isOpen && isOpen) {
      this.setState({ modifyingInvoiceNotes: invoiceNotes });
    }
  };

  render() {
    const { isOpen, selectedBillingLineItem, isNonBillable, modalType = PreviewInvoicesModalType.PREVIEW } = this.props;
    const {
      invoices,
      isLoading,
      isInvoiceNoteModalOpen,
      currentCustomerId,
      currentPaymentMethod,
      invoiceNoteModalType,
      modifyingInvoiceNotes,
    } = this.state;

    const numberOfLineItems = selectedBillingLineItem.length;

    return (
      <ActionModal
        title={modalType === PreviewInvoicesModalType.PREVIEW ? 'Preview invoices' : 'Preview or add notes to invoices'}
        isOpen={isOpen}
        onClose={this._onCloseModal}
        width="x2-large"
        verticalAlignment="highest"
      >
        {modalType === PreviewInvoicesModalType.PREVIEW_OR_EDIT && (
          <InvoiceNoteModal
            isOpen={isInvoiceNoteModalOpen}
            currentCustomerId={currentCustomerId}
            currentPaymentMethod={currentPaymentMethod}
            invoiceNoteModalType={invoiceNoteModalType}
            modifyingInvoiceNotes={modifyingInvoiceNotes}
            onSave={this._modifyNotes}
            onClose={this._closeAddNoteModal}
          />
        )}
        <WaiveLineItemActionModel
          isOpen={this.state.isWaiveModalOpen}
          onClose={this._closeWaiveInvoiceModal}
          type={'preview'}
          lineItems={this.state.selectedInvoice}
        />
        {!this.state.isLoading && (
          <div className="text-align-left">
            <div className="mb-medium">
              <Paragraph>
                This selection have {numberOfLineItems} line item{numberOfLineItems > 1 && 's'}.
              </Paragraph>
              <FilterSection
                availableFilters={availableFilters}
                filters={this.state.activeFilters}
                onChangeFilter={this._onChangeFilter}
                displayTimezone={null}
                usePortal={false}
                containerClassName="mv-small"
              />
              {modalType === PreviewInvoicesModalType.PREVIEW_OR_EDIT && (
                <Paragraph className="mb-large mt-small">Preview or add notes to your invoice below.</Paragraph>
              )}
              <div
                style={{ maxHeight: 'calc(100vh - 500px)', overflow: 'auto' }}
                className="bordered rounded-big mb-large"
              >
                <Row className="pv-medium bordered-bottom">
                  <Col span={8} className="pl-medium">
                    <SubTitle>Customer</SubTitle>
                  </Col>
                  <Col span={4}>
                    <SubTitle>Payment Method</SubTitle>
                  </Col>
                  <Col span={3}>
                    <SubTitle>Items</SubTitle>
                  </Col>
                  <Col span={4} className="text-align-right">
                    <SubTitle>Total</SubTitle>
                  </Col>
                  <Col span={3} />
                  <Col span={2} />
                </Row>
                {_.map(invoices, (invoice: IInvoice[]) => {
                  return (
                    <Row key={invoice[0].bookingBillingLineItemId} className="pv-medium evenodd">
                      <Col span={8} className="pl-medium">
                        {invoice[0].firstName} {invoice[0].lastName}
                      </Col>
                      <Col span={4}>
                        <PaymentMethodTag
                          paymentMethod={invoice[0].paymentMethod}
                          className="mr-small"
                          rounded={true}
                          size="small"
                        />
                      </Col>
                      <Col span={3}>
                        {invoice.length} line item{invoice.length !== 1 && 's'}
                      </Col>
                      <Col span={4} className="text-align-right">
                        {CommonUtils.formatPrice(
                          _.sumBy(invoice, (lineItem) => Number(lineItem.totalWithDiscount ?? lineItem.total)),
                        )}
                      </Col>
                      {modalType === PreviewInvoicesModalType.PREVIEW ? (
                        <Col span={3} className="text-align-center">
                          <HyperlinkButton
                            onClick={() => {
                              this._openInvoicePreview(invoice.map((lineItem) => lineItem.bookingBillingLineItemId));
                            }}
                          >
                            View Invoice
                          </HyperlinkButton>
                        </Col>
                      ) : (
                        <Col span={3} className="text-align-center flex align-center justify-end">
                          <Tooltip
                            placement="topLeft"
                            overlayClassName="white-tooltip fit-content-tooltip large-tooltip-arrow"
                            title={
                              <div
                                className="ph-medium pv-small whitespace-nowrap rounded-big bordered-top bordered-left bordered-right border shadow-without-top"
                                style={{ color: 'black' }}
                              >
                                Preview invoice
                              </div>
                            }
                          >
                            <HyperlinkButton
                              onClick={() => {
                                this._openInvoicePreview(
                                  invoice.map((lineItem) => lineItem.bookingBillingLineItemId),
                                  invoice[0].userId,
                                  invoice[0].paymentMethod,
                                );
                              }}
                            >
                              <svg style={{ marginLeft: 6 }} width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M2.75 12C2.75 11.6616 2.88577 11.0954 3.24331 10.409C3.59385 9.73591 4.13459 8.9926 4.88371 8.3011C6.37297 6.9264 8.69232 5.75 12 5.75C15.3077 5.75 17.627 6.9264 19.1163 8.3011C19.8654 8.9926 20.4061 9.73591 20.7567 10.409C21.1142 11.0954 21.25 11.6616 21.25 12C21.25 12.3384 21.1142 12.9046 20.7567 13.591C20.4061 14.2641 19.8654 15.0074 19.1163 15.6989C17.627 17.0736 15.3077 18.25 12 18.25C8.69232 18.25 6.37297 17.0736 4.88371 15.6989C4.13459 15.0074 3.59385 14.2641 3.24331 13.591C2.88577 12.9046 2.75 12.3384 2.75 12ZM12 4.25C8.30768 4.25 5.62703 5.5736 3.86629 7.1989C2.99041 8.0074 2.34365 8.88909 1.91294 9.71605C1.48923 10.5296 1.25 11.3384 1.25 12C1.25 12.6616 1.48923 13.4704 1.91294 14.284C2.34365 15.1109 2.99041 15.9926 3.86629 16.8011C5.62703 18.4264 8.30768 19.75 12 19.75C15.6923 19.75 18.373 18.4264 20.1337 16.8011C21.0096 15.9926 21.6564 15.1109 22.0871 14.284C22.5108 13.4704 22.75 12.6616 22.75 12C22.75 11.3384 22.5108 10.5296 22.0871 9.71605C21.6564 8.88909 21.0096 8.0074 20.1337 7.1989C18.373 5.5736 15.6923 4.25 12 4.25ZM9.7499 12C9.7499 10.7574 10.7573 9.75002 11.9999 9.75002C13.2425 9.75002 14.2499 10.7574 14.2499 12C14.2499 13.2427 13.2425 14.25 11.9999 14.25C10.7573 14.25 9.7499 13.2427 9.7499 12ZM11.9999 8.25002C9.92883 8.25002 8.2499 9.92896 8.2499 12C8.2499 14.0711 9.92883 15.75 11.9999 15.75C14.071 15.75 15.7499 14.0711 15.7499 12C15.7499 9.92896 14.071 8.25002 11.9999 8.25002Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </HyperlinkButton>
                          </Tooltip>
                          {modifyingInvoiceNotes.find(
                            (invoiceNote) =>
                              invoiceNote.userId === invoice[0].userId &&
                              invoiceNote.paymentMethod === invoice[0].paymentMethod,
                          ) ? (
                            <Tooltip
                              placement="topLeft"
                              overlayClassName="white-tooltip fit-content-tooltip large-tooltip-arrow"
                              title={
                                <div
                                  className="ph-medium pv-small whitespace-nowrap rounded-big bordered-top bordered-left bordered-right border shadow-without-top"
                                  style={{ color: 'black' }}
                                >
                                  Edit note
                                </div>
                              }
                            >
                              <HyperlinkButton
                                className="ml-12"
                                onClick={() =>
                                  this._openNoteModal(
                                    invoice[0].userId,
                                    invoice[0].paymentMethod,
                                    InvoiceNoteModalType.EDIT,
                                  )
                                }
                              >
                                <svg style={{ marginLeft: 10 }} width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5.35347 3.75004C4.72488 3.75038 4.26924 3.75336 3.90502 3.77938C3.50719 3.80781 3.2762 3.86092 3.10014 3.93773C2.57987 4.16473 2.16462 4.57997 1.93763 5.10024C1.86082 5.2763 1.8077 5.50729 1.77928 5.90513C1.75034 6.31015 1.74989 6.82821 1.74989 7.57143V15C1.74989 16.4354 1.75149 17.4365 1.85304 18.1919C1.9517 18.9257 2.13214 19.3142 2.4089 19.591C2.68567 19.8678 3.07424 20.0482 3.80802 20.1469C4.56337 20.2484 5.56448 20.25 6.9999 20.25H10.9999C12.4353 20.25 13.4364 20.2484 14.1918 20.1469C14.9256 20.0482 15.3141 19.8678 15.5909 19.591C15.8676 19.3142 16.0481 18.9257 16.1467 18.1919C16.2483 17.4365 16.2499 16.4354 16.2499 15V7.57143C16.2499 6.82821 16.2495 6.31015 16.2205 5.90513C16.1921 5.50729 16.139 5.2763 16.0622 5.10024C15.8352 4.57997 15.4199 4.16473 14.8997 3.93773C14.7236 3.86092 14.4926 3.80781 14.0948 3.77938C13.7306 3.75336 13.275 3.75038 12.6464 3.75004C12.3199 4.90427 11.2587 5.75 9.99994 5.75H7.99994C6.74118 5.75 5.67995 4.90427 5.35347 3.75004ZM12.6464 2.25002C13.2683 2.25027 13.7792 2.25301 14.2017 2.2832C14.6797 2.31735 15.1018 2.38939 15.4995 2.56289C16.3666 2.94121 17.0587 3.63329 17.437 4.50041C17.6105 4.89807 17.6825 5.32017 17.7167 5.79823C17.7499 6.263 17.7499 6.8347 17.7499 7.54345V7.57143V15V15.0549C17.7499 16.4225 17.7499 17.5248 17.6334 18.3918C17.5124 19.2919 17.2535 20.0497 16.6515 20.6517C16.0496 21.2536 15.2918 21.5125 14.3916 21.6335C13.5247 21.75 12.4224 21.75 11.0548 21.75H10.9999H6.9999H6.94503C5.57743 21.75 4.47511 21.75 3.60814 21.6335C2.70804 21.5125 1.95016 21.2536 1.34824 20.6517C0.746328 20.0497 0.487434 19.2919 0.366418 18.3918C0.249857 17.5248 0.249874 16.4225 0.249894 15.0549V15.0549V15V7.57143V7.5435V7.54345C0.249889 6.83471 0.249885 6.263 0.283091 5.79823C0.317246 5.32017 0.389287 4.89807 0.562786 4.50041C0.941104 3.63329 1.63319 2.94121 2.5003 2.56289C2.89797 2.38939 3.32007 2.31735 3.79813 2.2832C4.22065 2.25301 4.73155 2.25027 5.35345 2.25002C5.67991 1.09576 6.74116 0.25 7.99994 0.25H9.99994C11.2587 0.25 12.32 1.09576 12.6464 2.25002ZM6.74994 3C6.74994 2.30964 7.30959 1.75 7.99994 1.75H9.99994C10.6903 1.75 11.2499 2.30964 11.2499 3C11.2499 3.69036 10.6903 4.25 9.99994 4.25H7.99994C7.30959 4.25 6.74994 3.69036 6.74994 3ZM5.24989 10C5.24989 9.58579 5.58568 9.25 5.99989 9.25H11.9999C12.4141 9.25 12.7499 9.58579 12.7499 10C12.7499 10.4142 12.4141 10.75 11.9999 10.75H5.99989C5.58568 10.75 5.24989 10.4142 5.24989 10ZM5.99989 13.25C5.58568 13.25 5.24989 13.5858 5.24989 14C5.24989 14.4142 5.58568 14.75 5.99989 14.75H9.9999C10.4141 14.75 10.7499 14.4142 10.7499 14C10.7499 13.5858 10.4141 13.25 9.9999 13.25H5.99989Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </HyperlinkButton>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              placement="topLeft"
                              overlayClassName="white-tooltip fit-content-tooltip large-tooltip-arrow"
                              title={
                                <div
                                  className="ph-medium pv-small whitespace-nowrap rounded-big bordered-top bordered-left bordered-right shadow-without-top"
                                  style={{ color: 'black' }}
                                >
                                  Add note
                                </div>
                              }
                            >
                              <HyperlinkButton
                                className="ml-12"
                                onClick={() =>
                                  this._openNoteModal(
                                    invoice[0].userId,
                                    invoice[0].paymentMethod,
                                    InvoiceNoteModalType.ADD,
                                  )
                                }
                              >
                                <svg style={{ marginLeft: 10 }} width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M3.90502 3.77938C4.26924 3.75336 4.72488 3.75038 5.35347 3.75004C5.67995 4.90427 6.74118 5.75 7.99994 5.75H9.99994C11.2587 5.75 12.3199 4.90427 12.6464 3.75004C13.275 3.75038 13.7306 3.75336 14.0948 3.77938C14.4926 3.80781 14.7236 3.86092 14.8997 3.93773C15.4199 4.16473 15.8352 4.57997 16.0622 5.10024C16.139 5.2763 16.1921 5.50729 16.2205 5.90513C16.2495 6.31015 16.2499 6.82821 16.2499 7.57143V15C16.2499 16.4354 16.2483 17.4365 16.1467 18.1919C16.0481 18.9257 15.8676 19.3142 15.5909 19.591C15.3141 19.8678 14.9256 20.0482 14.1918 20.1469C13.4364 20.2484 12.4353 20.25 10.9999 20.25H6.9999C5.56448 20.25 4.56337 20.2484 3.80802 20.1469C3.07424 20.0482 2.68567 19.8678 2.4089 19.591C2.13214 19.3142 1.9517 18.9257 1.85304 18.1919C1.75149 17.4365 1.74989 16.4354 1.74989 15V7.57143C1.74989 6.82821 1.75034 6.31015 1.77928 5.90513C1.8077 5.50729 1.86082 5.2763 1.93763 5.10024C2.16462 4.57997 2.57987 4.16473 3.10014 3.93773C3.2762 3.86092 3.50719 3.80781 3.90502 3.77938ZM14.2017 2.2832C13.7792 2.25301 13.2683 2.25027 12.6464 2.25002C12.32 1.09576 11.2587 0.25 9.99994 0.25H7.99994C6.74116 0.25 5.67991 1.09576 5.35345 2.25002C4.73155 2.25027 4.22065 2.25301 3.79813 2.2832C3.32007 2.31735 2.89797 2.38939 2.5003 2.56289C1.63319 2.94121 0.941104 3.63329 0.562786 4.50041C0.389287 4.89807 0.317246 5.32017 0.283091 5.79823C0.249885 6.263 0.249889 6.83471 0.249894 7.54345V7.5435V7.57143V15V15.0549V15.0549C0.249874 16.4225 0.249857 17.5248 0.366418 18.3918C0.487434 19.2919 0.746328 20.0497 1.34824 20.6517C1.95016 21.2536 2.70804 21.5125 3.60814 21.6335C4.47511 21.75 5.57743 21.75 6.94503 21.75H6.9999H10.9999H11.0548C12.4224 21.75 13.5247 21.75 14.3916 21.6335C15.2918 21.5125 16.0496 21.2536 16.6515 20.6517C17.2535 20.0497 17.5124 19.2919 17.6334 18.3918C17.7499 17.5248 17.7499 16.4225 17.7499 15.0549V15V7.57143V7.54345C17.7499 6.8347 17.7499 6.263 17.7167 5.79823C17.6825 5.32017 17.6105 4.89807 17.437 4.50041C17.0587 3.63329 16.3666 2.94121 15.4995 2.56289C15.1018 2.38939 14.6797 2.31735 14.2017 2.2832ZM7.99994 1.75C7.30959 1.75 6.74994 2.30964 6.74994 3C6.74994 3.69036 7.30959 4.25 7.99994 4.25H9.99994C10.6903 4.25 11.2499 3.69036 11.2499 3C11.2499 2.30964 10.6903 1.75 9.99994 1.75H7.99994ZM9.7499 9.5C9.7499 9.08579 9.41411 8.75 8.9999 8.75C8.58568 8.75 8.2499 9.08579 8.2499 9.5V11.25H6.49989C6.08568 11.25 5.74989 11.5858 5.74989 12C5.74989 12.4142 6.08568 12.75 6.49989 12.75H8.2499V14.5C8.2499 14.9142 8.58568 15.25 8.9999 15.25C9.41411 15.25 9.7499 14.9142 9.7499 14.5V12.75H11.4999C11.9141 12.75 12.2499 12.4142 12.2499 12C12.2499 11.5858 11.9141 11.25 11.4999 11.25H9.7499V9.5Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </HyperlinkButton>
                            </Tooltip>
                          )}
                        </Col>
                      )}
                      <Col span={2} className="text-align-center">
                        {!isNonBillable && (
                          <Popover
                            content={
                              <Menu>
                                <Menu.Item
                                  text={
                                    <div onClick={() => this._openWaiveInvoiceModal(invoice)}>
                                      <Text>Waive invoice</Text>
                                    </div>
                                  }
                                  className="hover-bg-gray-lightest"
                                />
                              </Menu>
                            }
                            position={'bottom-right'}
                            usePortal={false}
                          >
                            {modalType === PreviewInvoicesModalType.PREVIEW ? (
                              <Icon type="ellipsis" className="text-size-x4-large cursor-pointer" />
                            ) : (
                              <Icon
                                type="ellipsis"
                                style={{ width: 28, height: 28 }}
                                className="text-size-x3-large cursor-pointer bordered rounded text-color-blue-action line-height-135"
                              />
                            )}
                          </Popover>
                        )}
                      </Col>
                    </Row>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {isLoading && <SpinningLoader size={100} message="Loading" />}
        <ActionModalFooter>
          {modalType === PreviewInvoicesModalType.PREVIEW ? (
            <PrimaryButton size="large" onClick={this._onCloseModal} loading={this.state.isLoading}>
              Close
            </PrimaryButton>
          ) : (
            <PrimaryButton size="large" onClick={this._onSaveChanges} loading={this.state.isLoading}>
              Save changes
            </PrimaryButton>
          )}
        </ActionModalFooter>
      </ActionModal>
    );
  }
}

const mapDispatch = (dispatch: IRootDispatch) => ({
  setSelectedCustomersForFilter: dispatch.customersStore.setSelectedCustomersForFilter,
});

export default connect(null, mapDispatch)(PreviewInvoicesModal);
